import React, { useRef, useCallback, useMemo, useState } from "react";
import {
  Card,
  CardContent,
  Button,
  Box,
  Typography,
  LinearProgress,
  Chip,
  IconButton,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Alert,
  List,
  ListItem,
  ListItemText,
} from "@mui/material";
import {
  FileDownload,
  FileUpload,
  Close,
  CheckCircle,
  Error,
  Download,
  ExpandMore,
  Info,
  Warning,
} from "@mui/icons-material";
import { ExportRequest, ValidationResult } from "../../api/import_export";
import { useImportExportJobs } from "./use-import-export-jobs";
import ExportConfirmationDialog from "./export-confirmation-dialog";
import ImportConfirmationDialog from "./import-confirmation-dialog";
import JobHistorySelector from "./job-history-selector";

interface ImportExportComponentProps {
  // Current filter state from the main component
  searchQuery: string;
  selectedBrandIds: string[];
  statusFilter: string;
  languageFilter: string;
  usedPhrasesOnly: boolean;
  suitabilityFilter: string;
  // Total phrases matching current filters
  totalPhrases?: number;
  // Map of brand IDs to brand names for display
  brandNamesMap?: Record<string, string>;
  // Callback when import completes to refresh phrases
  onImportCompleted?: () => void;
}

const ImportExportComponent: React.FC<ImportExportComponentProps> = ({
  searchQuery,
  selectedBrandIds,
  statusFilter,
  languageFilter,
  usedPhrasesOnly,
  suitabilityFilter,
  totalPhrases,
  brandNamesMap,
  onImportCompleted,
}) => {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [showExportConfirm, setShowExportConfirm] = useState(false);
  const [showImportConfirm, setShowImportConfirm] = useState(false);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [importValidation, setImportValidation] =
    useState<ValidationResult | null>(null);

  const {
    currentJob,
    jobHistory,
    handleExport,
    handleImport,
    handleCancelJob,
    handleDownloadResult,
    handlePickJob,
    validateImportFile,
    handleCloseJobDialog,
  } = useImportExportJobs({
    onJobCompleted: (job) => {
      if (job.job_type === "import" && job.status === "completed") {
        onImportCompleted?.();
      }
    },
  });

  // Create export request based on current filters
  const exportRequest = useMemo(
    (): ExportRequest => ({
      query: searchQuery || undefined,
      brand_ids: selectedBrandIds.length > 0 ? selectedBrandIds : undefined,
      status_filter: statusFilter || undefined,
      original_language_filter: languageFilter || undefined,
      used_phrases_only: usedPhrasesOnly,
      suitability_filter: suitabilityFilter || undefined,
    }),
    [
      searchQuery,
      selectedBrandIds,
      statusFilter,
      languageFilter,
      usedPhrasesOnly,
      suitabilityFilter,
    ]
  );

  const handleExportClick = useCallback(() => {
    setShowExportConfirm(true);
  }, []);

  const handleImportClick = useCallback(() => {
    fileInputRef.current?.click();
  }, []);

  const handleFileChange = useCallback(
    async (event: React.ChangeEvent<HTMLInputElement>) => {
      const file = event.target.files?.[0];
      if (file) {
        try {
          const validation = await validateImportFile(file);
          setSelectedFile(file);
          setImportValidation(validation);
          setShowImportConfirm(true);
        } catch (error) {
          setSelectedFile(file);
          setImportValidation({ success: false, error: String(error) });
          setShowImportConfirm(true);
        }
      }
      // Reset the input so the same file can be selected again
      event.target.value = "";
    },
    [validateImportFile]
  );

  const confirmExport = useCallback(() => {
    handleExport(exportRequest);
    setShowExportConfirm(false);
  }, [handleExport, exportRequest]);

  const confirmImport = useCallback(() => {
    if (selectedFile) {
      handleImport(selectedFile);
      setShowImportConfirm(false);
      setSelectedFile(null);
      setImportValidation(null);
    }
  }, [handleImport, selectedFile]);

  const getStatusColor = (
    status: string
  ):
    | "default"
    | "primary"
    | "secondary"
    | "error"
    | "info"
    | "success"
    | "warning" => {
    switch (status.toLowerCase()) {
      case "completed":
        return "success";
      case "failed":
        return "error";
      case "cancelled":
        return "warning";
      case "processing":
      case "validating":
        return "primary";
      default:
        return "default";
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status.toLowerCase()) {
      case "completed":
        return <CheckCircle fontSize="small" />;
      case "failed":
        return <Error fontSize="small" />;
      default:
        return null;
    }
  };

  const getProgressPercentage = (): number => {
    if (!displayJob) return 0;

    // Use real-time progress if available
    const progress = displayJob.progress;
    if (progress && progress.total > 0) {
      return Math.round((progress.processed / progress.total) * 100);
    }

    // Fall back to final results
    const results = displayJob.results;
    if (results && results.total && results.total > 0) {
      const processed = results.processed || results.exported || 0;
      return Math.round((processed / results.total) * 100);
    }

    return 0;
  };

  const displayJob = currentJob;
  const isInProgress =
    displayJob &&
    ["pending", "validating", "processing"].includes(
      displayJob.status.toLowerCase()
    );
  const isCompleted = displayJob?.status.toLowerCase() === "completed";
  const isFailed = displayJob?.status.toLowerCase() === "failed";

  const getJobSummary = () => {
    if (!displayJob) return { processed: 0, created: 0, updated: 0, errors: 0 };

    const processed =
      displayJob.progress?.processed ||
      displayJob.results?.processed ||
      displayJob.results?.exported ||
      0;
    const created =
      displayJob.progress?.created || displayJob.results?.created || 0;
    const updated =
      displayJob.progress?.updated || displayJob.results?.updated || 0;
    const errors =
      displayJob.progress?.errors || displayJob.results?.errors || 0;

    return { processed, created, updated, errors };
  };

  const { processed, created, updated, errors } = getJobSummary();

  return (
    <>
      <Card sx={{ mb: 3 }}>
        <CardContent>
          {/* Header with job history */}
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <Typography variant="h6">Import / Export</Typography>
            <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
              {jobHistory.length > 0 && (
                <JobHistorySelector
                  jobs={jobHistory}
                  selectedJobId={currentJob?.id || ""}
                  onPickJob={handlePickJob}
                />
              )}

              <Button
                variant="outlined"
                startIcon={<FileDownload />}
                onClick={handleExportClick}
                size="small"
                disabled={!!isInProgress}
              >
                Export
              </Button>

              <Button
                variant="outlined"
                startIcon={<FileUpload />}
                onClick={handleImportClick}
                size="small"
                disabled={!!isInProgress}
              >
                Import
              </Button>
            </Box>
          </Box>

          {/* Current/Selected Job Display */}
          {displayJob && (
            <>
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  mb: 1,
                }}
              >
                <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                  <Typography variant="subtitle1">
                    {displayJob.job_type === "import" ? "Import" : "Export"}:{" "}
                    {displayJob.filename}
                  </Typography>
                  <Chip
                    label={displayJob.status.toUpperCase()}
                    color={getStatusColor(displayJob.status)}
                    icon={getStatusIcon(displayJob.status) || undefined}
                    size="small"
                    variant={isInProgress ? "filled" : "outlined"}
                  />
                </Box>

                {!currentJob && (
                  <IconButton
                    size="small"
                    onClick={() => handleCloseJobDialog()}
                    aria-label="dismiss"
                  >
                    <Close />
                  </IconButton>
                )}
              </Box>

              {/* Progress Bar - only for active jobs */}
              {isInProgress && currentJob && (
                <Box sx={{ mb: 2 }}>
                  <Box
                    display="flex"
                    justifyContent="space-between"
                    alignItems="center"
                    mb={1}
                  >
                    <Typography variant="body2">
                      {displayJob.progress?.message || "Processing..."}
                    </Typography>
                    <Box display="flex" alignItems="center" gap={2}>
                      <Typography variant="body2">
                        {getProgressPercentage()}%
                      </Typography>
                      <Button
                        variant="outlined"
                        color="warning"
                        size="small"
                        onClick={() => handleCancelJob(currentJob.id)}
                      >
                        Cancel
                      </Button>
                    </Box>
                  </Box>
                  <LinearProgress
                    variant="determinate"
                    value={getProgressPercentage()}
                    sx={{ height: 6, borderRadius: 3 }}
                  />
                </Box>
              )}

              {/* Job Summary */}
              {(isCompleted || isFailed) && (
                <Box sx={{ mb: 2 }}>
                  <Typography variant="body2">
                    {displayJob.job_type === "import" ? (
                      <>
                        {isCompleted ? "✓ " : "✗ "}
                        {processed.toLocaleString()} processed
                        {created > 0 && `, ${created.toLocaleString()} created`}
                        {updated > 0 && `, ${updated.toLocaleString()} updated`}
                        {errors > 0 && `, ${errors.toLocaleString()} errors`}
                      </>
                    ) : (
                      <>
                        {isCompleted ? "✓ " : "✗ "}
                        {processed.toLocaleString()} phrases exported
                      </>
                    )}
                  </Typography>
                </Box>
              )}

              {/* Job Details */}
              {(displayJob.errors ||
                displayJob.filter_context ||
                displayJob.results ||
                isFailed) && (
                <Box>
                  {/* Detailed Results */}
                  {displayJob.results && (
                    <Accordion>
                      <AccordionSummary expandIcon={<ExpandMore />}>
                        <Box
                          sx={{ display: "flex", alignItems: "center", gap: 1 }}
                        >
                          <Info color="primary" fontSize="small" />
                          <Typography variant="subtitle2">
                            Detailed Results
                          </Typography>
                        </Box>
                      </AccordionSummary>
                      <AccordionDetails>
                        <Box
                          sx={{
                            display: "grid",
                            gridTemplateColumns:
                              "repeat(auto-fit, minmax(120px, 1fr))",
                            gap: 1,
                          }}
                        >
                          {displayJob.results.total !== undefined && (
                            <Box>
                              <Typography
                                variant="caption"
                                color="text.secondary"
                              >
                                Total
                              </Typography>
                              <Typography variant="h6">
                                {displayJob.results.total.toLocaleString()}
                              </Typography>
                            </Box>
                          )}
                          {displayJob.results.processed !== undefined && (
                            <Box>
                              <Typography
                                variant="caption"
                                color="text.secondary"
                              >
                                Processed
                              </Typography>
                              <Typography variant="h6">
                                {displayJob.results.processed.toLocaleString()}
                              </Typography>
                            </Box>
                          )}
                          {displayJob.results.created !== undefined &&
                            displayJob.results.created > 0 && (
                              <Box>
                                <Typography
                                  variant="caption"
                                  color="text.secondary"
                                >
                                  Created
                                </Typography>
                                <Typography variant="h6" color="success.main">
                                  {displayJob.results.created.toLocaleString()}
                                </Typography>
                              </Box>
                            )}
                          {displayJob.results.updated !== undefined &&
                            displayJob.results.updated > 0 && (
                              <Box>
                                <Typography
                                  variant="caption"
                                  color="text.secondary"
                                >
                                  Updated
                                </Typography>
                                <Typography variant="h6" color="info.main">
                                  {displayJob.results.updated.toLocaleString()}
                                </Typography>
                              </Box>
                            )}
                          {displayJob.results.exported !== undefined && (
                            <Box>
                              <Typography
                                variant="caption"
                                color="text.secondary"
                              >
                                Exported
                              </Typography>
                              <Typography variant="h6" color="primary.main">
                                {displayJob.results.exported.toLocaleString()}
                              </Typography>
                            </Box>
                          )}
                          {displayJob.results.errors !== undefined &&
                            displayJob.results.errors > 0 && (
                              <Box>
                                <Typography
                                  variant="caption"
                                  color="text.secondary"
                                >
                                  Errors
                                </Typography>
                                <Typography variant="h6" color="error.main">
                                  {displayJob.results.errors.toLocaleString()}
                                </Typography>
                              </Box>
                            )}
                        </Box>
                      </AccordionDetails>
                    </Accordion>
                  )}

                  {/* Error Details */}
                  {(isFailed ||
                    (displayJob.errors?.errors &&
                      displayJob.errors.errors.length > 0)) && (
                    <Accordion>
                      <AccordionSummary expandIcon={<ExpandMore />}>
                        <Box
                          sx={{ display: "flex", alignItems: "center", gap: 1 }}
                        >
                          <Warning color="error" fontSize="small" />
                          <Typography variant="subtitle2">
                            {displayJob.errors?.errors &&
                            displayJob.errors.errors.length > 0
                              ? `Errors (${displayJob.errors.errors.length})`
                              : "Job Failed"}
                          </Typography>
                        </Box>
                      </AccordionSummary>
                      <AccordionDetails>
                        {displayJob.errors?.errors &&
                        displayJob.errors.errors.length > 0 ? (
                          <Box
                            sx={{
                              maxHeight: 300,
                              overflow: "auto",
                              border: "1px solid",
                              borderColor: "divider",
                              borderRadius: 1,
                              backgroundColor: "background.paper",
                            }}
                          >
                            <List dense sx={{ py: 0, my: 0 }}>
                              {displayJob.errors.errors.map((error, index) => (
                                <ListItem
                                  key={index}
                                  sx={{
                                    pl: 2,
                                    borderBottom:
                                      index <
                                      displayJob.errors!.errors.length - 1
                                        ? "1px solid"
                                        : "none",
                                    borderColor: "divider",
                                  }}
                                >
                                  <ListItemText
                                    primary={error.error}
                                    secondary={
                                      error.row
                                        ? `Row: ${error.row}`
                                        : undefined
                                    }
                                  />
                                </ListItem>
                              ))}
                            </List>
                          </Box>
                        ) : (
                          <Alert severity="error">
                            <Typography variant="body2">
                              The job failed but no specific error details are
                              available. Check the server logs or try again.
                            </Typography>
                          </Alert>
                        )}
                      </AccordionDetails>
                    </Accordion>
                  )}

                  {/* Export Filter Context */}
                  {displayJob.job_type === "export" &&
                    displayJob.filter_context && (
                      <Accordion>
                        <AccordionSummary expandIcon={<ExpandMore />}>
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: 1,
                            }}
                          >
                            <Info color="primary" fontSize="small" />
                            <Typography variant="subtitle2">
                              Export Filters
                            </Typography>
                          </Box>
                        </AccordionSummary>
                        <AccordionDetails>
                          <Box
                            sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}
                          >
                            {displayJob.filter_context.query && (
                              <Chip
                                label={`Search: "${displayJob.filter_context.query}"`}
                                size="small"
                                variant="outlined"
                              />
                            )}
                            {displayJob.filter_context.brand_ids?.length && (
                              <Chip
                                label={`${displayJob.filter_context.brand_ids.length} brands`}
                                size="small"
                                variant="outlined"
                              />
                            )}
                            {displayJob.filter_context.status_filter && (
                              <Chip
                                label={`Status: ${displayJob.filter_context.status_filter}`}
                                size="small"
                                variant="outlined"
                              />
                            )}
                            {displayJob.filter_context
                              .original_language_filter && (
                              <Chip
                                label={`Language: ${displayJob.filter_context.original_language_filter}`}
                                size="small"
                                variant="outlined"
                              />
                            )}
                            {displayJob.filter_context.used_phrases_only && (
                              <Chip
                                label="Used phrases only"
                                size="small"
                                variant="outlined"
                              />
                            )}
                            {displayJob.filter_context.suitability_filter && (
                              <Chip
                                label={`Suitable for: ${displayJob.filter_context.suitability_filter}`}
                                size="small"
                                variant="outlined"
                              />
                            )}
                            {!displayJob.filter_context.query &&
                              !displayJob.filter_context.brand_ids?.length &&
                              !displayJob.filter_context.status_filter &&
                              !displayJob.filter_context
                                .original_language_filter &&
                              !displayJob.filter_context.used_phrases_only &&
                              !displayJob.filter_context.suitability_filter && (
                                <Typography
                                  variant="body2"
                                  color="text.secondary"
                                >
                                  No filters applied (all phrases exported)
                                </Typography>
                              )}
                          </Box>
                        </AccordionDetails>
                      </Accordion>
                    )}
                </Box>
              )}

              {/* Actions */}
              {(isCompleted || isFailed) && (
                <Box
                  sx={{ display: "flex", justifyContent: "flex-end", mt: 2 }}
                >
                  <Button
                    variant="contained"
                    startIcon={<Download />}
                    size="small"
                    onClick={() => handleDownloadResult(displayJob.id)}
                  >
                    Download{" "}
                    {displayJob.job_type === "import"
                      ? "Original File"
                      : "Export File"}
                  </Button>
                </Box>
              )}
            </>
          )}

          {/* Hidden file input */}
          <input
            ref={fileInputRef}
            type="file"
            accept=".csv"
            style={{ display: "none" }}
            onChange={handleFileChange}
          />
        </CardContent>
      </Card>

      {/* Confirmation Dialogs */}
      <ExportConfirmationDialog
        open={showExportConfirm}
        onClose={() => setShowExportConfirm(false)}
        onConfirm={confirmExport}
        exportRequest={exportRequest}
        totalPhrases={totalPhrases}
        brandNamesMap={brandNamesMap}
      />

      <ImportConfirmationDialog
        open={showImportConfirm}
        onClose={() => {
          setShowImportConfirm(false);
          setSelectedFile(null);
          setImportValidation(null);
        }}
        onConfirm={confirmImport}
        file={selectedFile}
        validation={importValidation}
      />
    </>
  );
};

export default ImportExportComponent;
